// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for merging contact records in the Hardfin system.
 * Provides type-safe contact merges with Zod validation.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { contactSchema } from '@/contact/api/types';

const mergeContactResponseSchema = z
  .object({
    data: contactSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, contact: data }));

type MergeContactResponse = z.infer<typeof mergeContactResponseSchema>;

export interface MergeContactParameters {
  removedContactID: string;
  firstName: string;
  lastName: string;
  customerId: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  comments: string | null;
  isArchived: boolean;
}

export async function mergeContact(
  key: string,
  parameters: MergeContactParameters
): Promise<MergeContactResponse> {
  const url = `/v1/contact/${key}/merge`;
  const response = await api.post(url, parameters);

  return parseResponse(mergeContactResponseSchema, response.data, {
    schemaName: 'MergeContact',
    endpoint: url,
  });
}
